import React from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Building2, CreditCard, LogOut, ArrowLeft, Sparkles, Crown, Languages, Database, Store,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";

/**
 * PlatformAdminLayout — a deliberately minimal shell that is entirely separate
 * from the tenant-facing salon sidebar. It exposes ONLY the items the platform
 * owner needs:
 *
 *   • Dashboard
 *   • Tenants
 *   • Subscriptions
 *
 * Marketplace / WhatsApp / Reviews / Onboarding / Tenant signups are
 * intentionally hidden here. They still exist under their own routes and can
 * be re-exposed later without touching this layout.
 */
export default function PlatformAdminLayout() {
  const { user, logout } = useAuth() || {};
  const { lang, toggleLang } = useLanguage();
  const isAr = lang === "ar";
  const loc = useLocation();
  const navigate = useNavigate();

  // Only platform admins can use this surface. We render a polite block
  // rather than redirecting so misconfigured users see a clear reason.
  if (user && user.role !== "admin") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="max-w-md bg-white rounded-2xl border border-stone-100 p-8 text-center shadow-sm">
          <Sparkles className="mx-auto text-amber-500 mb-3" size={32}/>
          <h2 className="font-heading text-2xl text-stone-800">{isAr ? "وصول مقيّد" : "Restricted area"}</h2>
          <p className="text-sm text-stone-500 mt-2">{isAr ? "هذه المنطقة مخصصة لمالك المنصة فقط." : "This area is reserved for the platform owner."}</p>
          <button onClick={() => navigate("/dashboard")} className="mt-5 btn-gold px-5 py-2 rounded-lg">{isAr ? "العودة للنظام" : "Back to app"}</button>
        </div>
      </div>
    );
  }

  const items = [
    { to: "/platform-admin",                 label: isAr ? "لوحة المنصة"     : "Dashboard",     Icon: LayoutDashboard, exact: true },
    { to: "/platform-admin/tenants",         label: isAr ? "المستأجرون"      : "Tenants",       Icon: Building2 },
    { to: "/platform-admin/subscriptions",   label: isAr ? "الاشتراكات"       : "Subscriptions", Icon: CreditCard },
    { to: "/platform-admin/plans",           label: isAr ? "تعريف الخطط"      : "Plans",          Icon: Crown },
    { to: "/platform-admin/marketplace",     label: isAr ? "الماركت بليس"     : "Marketplace",    Icon: Store },
    { to: "/platform-admin/backups",         label: isAr ? "النسخ الاحتياطي"  : "Backups",        Icon: Database },
  ];

  const isActive = (it) => it.exact ? loc.pathname === it.to : loc.pathname.startsWith(it.to);

  return (
    <div className="min-h-screen flex bg-stone-50" dir={isAr ? "rtl" : "ltr"}>
      {/* Sidebar */}
      <aside className="w-64 bg-gradient-to-b from-stone-900 to-stone-800 text-white p-4 flex flex-col shrink-0 sticky top-0 h-screen">
        <div className="flex items-center gap-2 mb-6 px-2">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-rose-500 flex items-center justify-center shadow-md">
            <Sparkles size={16}/>
          </div>
          <div>
            <div className="font-heading text-base leading-none">{isAr ? "إدارة المنصة" : "Platform Admin"}</div>
            <div className="text-[10px] text-stone-400 mt-1">{isAr ? "تحكم المالك" : "Owner Control"}</div>
          </div>
        </div>

        <nav className="space-y-1 flex-1">
          {items.map((it) => {
            const active = isActive(it);
            return (
              <Link key={it.to} to={it.to} data-testid={`pa-nav-${it.to.split("/").pop() || "root"}`}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition ${active ? "bg-white/10 text-white shadow-inner" : "text-stone-300 hover:bg-white/5 hover:text-white"}`}>
                <it.Icon size={16}/>{it.label}
              </Link>
            );
          })}
        </nav>

        <div className="mt-4 pt-4 border-t border-white/10 space-y-1">
          <button onClick={toggleLang} data-testid="pa-lang-toggle" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-stone-300 hover:bg-white/5 hover:text-white transition">
            <Languages size={14}/>{isAr ? "English" : "العربية"}
          </button>
          <Link to="/dashboard" className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-stone-300 hover:bg-white/5 hover:text-white transition">
            <ArrowLeft size={14} className={isAr ? "rotate-180" : ""}/>{isAr ? "تطبيق الصالون" : "Tenant App"}
          </Link>
          {logout && (
            <button onClick={() => logout()} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-stone-300 hover:bg-rose-500/15 hover:text-rose-300 transition">
              <LogOut size={14}/>{isAr ? "خروج" : "Sign out"}
            </button>
          )}
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}
